import { TYPE_META } from "@/components/layers";
import { TextField, AreaField, SelectField, SwitchField, MediaField } from "@/components/editor/fields";
import { QuizBuilder } from "@/components/editor/QuizBuilder";
import { AppearanceFields } from "@/components/editor/AppearanceFields";

const ANIMS = [["none", "None"], ["fade", "Fade"], ["slide", "Slide"], ["scale", "Scale"], ["zoom", "Zoom"], ["bounce", "Bounce"], ["pulse", "Pulse"], ["rotate", "Rotate"]];
const HS_STYLES = [["pulse", "Pulse"], ["glow", "Glow"], ["circle", "Circle"], ["arrow", "Arrow"], ["icon", "Icon"], ["invisible", "Invisible"], ["image", "Custom image"]];
const ACTIONS = [["popup", "Open popup"], ["audio", "Play audio"], ["video", "Play video"], ["image", "Open image"], ["url", "Open webpage"], ["page", "Jump to page"]];

const Section = ({ title, children }) => (
  <div className="space-y-3 border-b border-[#DCE3EE] pb-4">
    <p className="font-mono text-[10px] uppercase tracking-widest text-[#3D3DF0]">{title}</p>
    {children}
  </div>
);

const ActionFields = ({ l, set, bookId }) => {
  switch (l.action) {
    case "popup": return <><TextField label="Popup title" value={l.title} onChange={(v) => set({ title: v })} testid="prop-title" /><AreaField label="Popup text" value={l.content} onChange={(v) => set({ content: v })} testid="prop-content" /></>;
    case "audio": return <MediaField label="Audio file" value={l.src} onChange={(v) => set({ src: v })} accept="audio/*" bookId={bookId} testid="prop-src" />;
    case "video": return <><MediaField label="Video (MP4 or YouTube/Vimeo URL)" value={l.src} onChange={(v) => set({ src: v })} accept="video/*" bookId={bookId} testid="prop-src" /><MediaField label="Poster image" value={l.poster} onChange={(v) => set({ poster: v })} accept="image/*" bookId={bookId} testid="prop-poster" /></>;
    case "image": return <MediaField label="Image" value={l.src} onChange={(v) => set({ src: v })} accept="image/*" bookId={bookId} testid="prop-src" />;
    case "url": return <TextField label="Webpage URL" value={l.url} onChange={(v) => set({ url: v })} testid="prop-url" />;
    case "page": return <TextField label="Target page" type="number" value={l.page} onChange={(v) => set({ page: v })} testid="prop-page" />;
    default: return null;
  }
};

const TypeFields = ({ l, set, bookId }) => {
  switch (l.type) {
    case "hotspot": return (
      <>
        <SelectField label="Style" value={l.style} onChange={(v) => set({ style: v })} options={HS_STYLES} testid="prop-style" />
        {l.style === "image" && <MediaField label="Custom icon (image/SVG)" value={l.icon} onChange={(v) => set({ icon: v })} accept="image/*" bookId={bookId} testid="prop-icon" />}
        <SelectField label="On click" value={l.action} onChange={(v) => set({ action: v })} options={ACTIONS} testid="prop-action" />
        <ActionFields l={l} set={set} bookId={bookId} />
      </>
    );
    case "audio": return (
      <>
        <MediaField label="Audio file (MP3, WAV…)" value={l.src} onChange={(v) => set({ src: v })} accept="audio/*" bookId={bookId} testid="prop-src" />
        <SwitchField label="Play when page opens" checked={l.autoplay} onChange={(v) => set({ autoplay: v })} testid="prop-autoplay" />
        <SwitchField label="Loop" checked={l.loop} onChange={(v) => set({ loop: v })} testid="prop-loop" />
        {l.content && <AreaField label="Narration script" value={l.content} onChange={(v) => set({ content: v })} testid="prop-content" />}
      </>
    );
    case "video": return (
      <>
        <MediaField label="Video (MP4 or YouTube/Vimeo URL)" value={l.src} onChange={(v) => set({ src: v })} accept="video/*" bookId={bookId} testid="prop-src" />
        <MediaField label="Poster image" value={l.poster} onChange={(v) => set({ poster: v })} accept="image/*" bookId={bookId} testid="prop-poster" />
        <SelectField label="Mode" value={l.mode} onChange={(v) => set({ mode: v })} options={[["inline", "Inline on page"], ["popup", "Popup modal"]]} testid="prop-mode" />
        <SwitchField label="Autoplay on page arrival (muted)" checked={l.autoplay} onChange={(v) => set({ autoplay: v })} testid="prop-autoplay" />
        <SwitchField label="Loop" checked={l.loop} onChange={(v) => set({ loop: v })} testid="prop-loop" />
        <SwitchField label="Show controls" checked={l.controls} onChange={(v) => set({ controls: v })} testid="prop-controls" />
        <div className="grid grid-cols-2 gap-2">
          <TextField label="Start (s)" type="number" value={l.start} onChange={(v) => set({ start: v })} testid="prop-start" />
          <TextField label="End (s)" type="number" value={l.end} onChange={(v) => set({ end: v })} testid="prop-end" />
        </div>
      </>
    );
    case "image": return (
      <>
        <MediaField label="Image" value={l.src} onChange={(v) => set({ src: v })} accept="image/*" bookId={bookId} testid="prop-src" />
        <SwitchField label="Open lightbox on click" checked={l.lightbox} onChange={(v) => set({ lightbox: v })} testid="prop-lightbox" />
      </>
    );
    case "popup": return (
      <>
        <SelectField label="Trigger style" value={l.style} onChange={(v) => set({ style: v })} options={[["icon", "Info icon"], ["button", "Button"], ["invisible", "Invisible area"]]} testid="prop-style" />
        {l.style === "button" && <TextField label="Button label" value={l.label} onChange={(v) => set({ label: v })} testid="prop-label" />}
        <TextField label="Title" value={l.title} onChange={(v) => set({ title: v })} testid="prop-title" />
        <AreaField label="Content" value={l.content} onChange={(v) => set({ content: v })} testid="prop-content" />
        <MediaField label="Image (optional)" value={l.src} onChange={(v) => set({ src: v })} accept="image/*" bookId={bookId} testid="prop-src" />
      </>
    );
    case "link": return (
      <>
        <SelectField label="Style" value={l.style} onChange={(v) => set({ style: v })} options={[["outline", "Highlighted"], ["invisible", "Invisible"]]} testid="prop-style" />
        <TextField label="URL (leave blank to jump to page)" value={l.url} onChange={(v) => set({ url: v })} testid="prop-url" />
        <TextField label="Or jump to page" type="number" value={l.page} onChange={(v) => set({ page: v })} testid="prop-page" />
      </>
    );
    case "quiz": return <QuizBuilder l={l} set={set} />;
    case "text": return (
      <>
        <AreaField label="Text" value={l.content} onChange={(v) => set({ content: v })} testid="prop-content" />
        <div className="grid grid-cols-3 gap-2">
          <TextField label="Color" value={l.color} onChange={(v) => set({ color: v })} testid="prop-color" />
          <TextField label="Fill" value={l.bg} onChange={(v) => set({ bg: v })} testid="prop-bg" />
          <TextField label="Size" type="number" value={l.fontSize} onChange={(v) => set({ fontSize: v })} testid="prop-fontsize" />
        </div>
      </>
    );
    default: return null;
  }
};

export const PropertiesPanel = ({ layer, bookId, onChange }) => {
  if (!layer) return <p className="py-10 text-center text-xs text-[#64748B]" data-testid="properties-empty">Select a layer on the canvas to edit its properties.</p>;
  const set = (patch) => onChange(layer.id, patch);
  const Icon = TYPE_META[layer.type].icon;
  return (
    <div className="space-y-4" data-testid="properties-panel">
      <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-[#3D3DF0]" /><span className="font-display text-lg font-bold">{TYPE_META[layer.type].label}</span></div>
      <TextField label="Layer name" value={layer.name} onChange={(v) => set({ name: v })} testid="prop-name" />
      <Section title="Position & size (%)">
        <div className="grid grid-cols-4 gap-2 font-mono">
          {["x", "y", "w", "h"].map((k) => <TextField key={k} label={k.toUpperCase()} type="number" value={layer[k]} onChange={(v) => set({ [k]: v ?? 0 })} testid={`prop-${k}`} />)}
        </div>
      </Section>
      <Section title="Content"><TypeFields l={layer} set={set} bookId={bookId} /></Section>
      {["hotspot", "audio", "popup", "quiz"].includes(layer.type) && <Section title="Appearance & motion"><AppearanceFields l={layer} set={set} /></Section>}
      <Section title="Animation">
        <SelectField label="Entrance" value={layer.anim || "none"} onChange={(v) => set({ anim: v })} options={ANIMS} testid="prop-anim" />
        <TextField label="Delay (s)" type="number" value={layer.animDelay} onChange={(v) => set({ animDelay: v ?? 0 })} testid="prop-anim-delay" />
      </Section>
      <Section title="Accessibility">
        <TextField label="Alt text / screen-reader label" value={layer.alt} onChange={(v) => set({ alt: v })} testid="prop-alt" />
      </Section>
    </div>
  );
};
